import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DrawerSectionTitleProps {
  /** Optional extra CSS classes on the outer wrapper */
  className?: string;
  /** Lucide icon rendered inside the primary-tinted badge */
  icon: LucideIcon;
  /** Section title */
  title: React.ReactNode;
  /** Optional inline suffix rendered right after the title */
  titleSuffix?: React.ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Icon-chip + title header, extracted from `DrawerSection` so it can be reused
 * standalone (e.g. above a table card that isn't wrapped in a `DrawerSection`).
 * `DrawerSection` renders this internally — no visual change.
 */
export function DrawerSectionTitle({
  className,
  icon: Icon,
  title,
  titleSuffix,
}: DrawerSectionTitleProps): React.JSX.Element {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 text-primary shrink-0">
        <Icon className="w-3.5 h-3.5" />
      </div>
      <span className="text-sm font-semibold text-foreground flex items-center gap-1 min-w-0">
        {title}
        {titleSuffix !== undefined && titleSuffix}
      </span>
    </div>
  );
}
